import { FC, useState } from "react";
import { Typography, Box, IconButton, Card, useTheme } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { Chart } from "../CustomChart/types";
import { CustomChart } from "../CustomChart";
import { RevenueList } from "../ChartList/RevenueList";
import { RatioList } from "../ChartList/RatioList";

type ChartCarrouselProps = {
  chartSources: Chart[];
};

const MOCK_REVENUE_ITEMS: { label: string; value: number }[] = [
  {
    label: "YESTERDAY",
    value: 0.0,
  },
  {
    label: "TODAY",
    value: 25.746,
  },
  {
    label: "MTD",
    value: 38.662,
  },
  {
    label: "YTD",
    value: 2_942_299,
  },
];

const MOCK_RATIO_ITEMS: { label: string; value: number }[] = [
  {
    label: "YESTERDAY",
    value: 0,
  },
  {
    label: "TODAY",
    value: 20,
  },
  {
    label: "MTD",
    value: 20,
  },
  {
    label: "YTD",
    value: 25,
  },
];

export const ChartCarrousel: FC<ChartCarrouselProps> = ({ chartSources }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const totalCharts = chartSources.length;
  const theme = useTheme();

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? totalCharts - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === totalCharts - 1 ? 0 : prev + 1));
  };

  const currentChart = chartSources[currentIndex];

  if (!currentChart) return null;

  const isChartWithCardStyle: boolean =
    currentChart.chartType === "Bars" || currentChart.chartType === "Lines";

  const revenueData = currentChart.chartData.map((val) => {
    return {
      label: val.labelName,
      value: val.dataValue1 as number,
    };
  });
  const ratioData = currentChart.chartData.map((val) => {
    return {
      label: val.labelName,
      value: (val.dataValue1 as number) * 100,
    };
  });

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        width="100%"
        sx={{ px: 2 }}
      >
        <IconButton
          onClick={handlePrev}
          sx={{
            position: "absolute",
            left: 0,
            zIndex: 1,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <ArrowBackIosNew />
        </IconButton>
        <Card
          elevation={isChartWithCardStyle ? 3 : 0}
          sx={{
            borderRadius: 3,
            p: 3,
            width: "100%",
            maxWidth: 1000,
            maxHeight: 800,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: isChartWithCardStyle ? "white" : "transparent",
            boxShadow: isChartWithCardStyle ? undefined : "none",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            textAlign="left"
            sx={{
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              pl: 1.5,
            }}
          >
            {currentChart.description}
          </Typography>

          <Box
            sx={{
              width: "100%",
              maxHeight: 800,
              overflowY: "auto",
              pr: 1,
            }}
          >
            {(currentChart.chartType === "Bars" ||
              currentChart.chartType === "Lines") && (
              <CustomChart chart={currentChart} />
            )}
            {currentChart.chartType === "Revenue" && (
              <RevenueList
                label={currentChart.description}
                items={revenueData}
              />
            )}
            {currentChart.chartType === "Ratio" && (
              <RatioList label={currentChart.description} items={ratioData} />
            )}
          </Box>
        </Card>
        <IconButton
          onClick={handleNext}
          sx={{
            position: "absolute",
            right: 0,
            zIndex: 1,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <ArrowForwardIos />
        </IconButton>
      </Box>
      <Box display="flex" justifyContent="center" mt={2}>
        {chartSources.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              mx: 0.5,
              backgroundColor:
                index === currentIndex ? "primary.main" : "grey.400",
              transition: "background-color 0.3s ease",
            }}
          />
        ))}
      </Box>
    </>
  );
};
